import React, { useEffect, useState } from "react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../config/firebase";
import PageTemplate from "../templates/PageTemplate";
import { Card, CardHeader, CardContent, CardTitle } from "../components/Card";

export default function ConfigPayments() {
  const [values, setValues] = useState({ adminCommission: "", tva: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    async function fetchConfig() {
      setLoading(true);
      const ref = doc(db, "config", "payments");
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data();
        setValues({
          adminCommission: data.adminCommission ?? "",
          tva: data.tva ?? "",
        });
      }
      setLoading(false);
    }
    fetchConfig();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((v) => ({ ...v, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const ref = doc(db, "config", "payments");
      await updateDoc(ref, {
        adminCommission: Number(values.adminCommission),
        tva: Number(values.tva),
      });

      // Success notification
      const successDiv = document.createElement("div");
      successDiv.className =
        "fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2";
      successDiv.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
        </svg>
        Configuration enregistrée avec succès !
      `;
      document.body.appendChild(successDiv);
      setTimeout(() => successDiv.remove(), 3000);
    } catch (e) {
      alert("Erreur lors de l'enregistrement.");
    }
    setSaving(false);
  };

  // Calculer les montants exemple
  const exampleAmount = 100;
  const commissionAmount = (exampleAmount * (values.adminCommission || 0)) / 100;
  const tvaAmount = (exampleAmount * (values.tva || 0)) / 100;
  const totalWithTva = commissionAmount + tvaAmount;
  const couturierAmount = exampleAmount - totalWithTva;

  return (
    <PageTemplate
      loading={loading}
      content={
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Info Card */}
          <Card className="bg-gradient-to-r from-pink-50 to-rose-50 border-pink-200">
            <CardContent className="flex items-start gap-4">
              <div className="p-3 bg-[#ff9be4] rounded-lg">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-6 h-6 text-white"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M2.25 18.75a60.07 60.07 0 0 1 15.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 0 1 3 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 0 0-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 0 1-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 0 0 3 15h-.75M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm3 0h.008v.008H18V10.5Zm-12 0h.008v.008H6V10.5Z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">
                  Configuration des paiements
                </h3>
                <p className="text-sm text-gray-600">
                  Définissez les paramètres de commission et de TVA appliqués aux
                  transactions.
                </p>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Configuration Form */}
            <Card>
              <CardHeader>
                <CardTitle>Paramètres</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Admin Commission */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Commission Admin
                    <span className="text-pink-500 ml-1">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      name="adminCommission"
                      value={values.adminCommission}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2.5 pr-12 focus:outline-none focus:ring-2 focus:ring-[#ff9be4] focus:border-transparent transition-all"
                      min="0"
                      max="100"
                      step="0.01"
                      placeholder="Ex: 15"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">
                      %
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Pourcentage prélevé sur chaque transaction
                  </p>
                </div>

                {/* TVA */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    TVA
                    <span className="text-pink-500 ml-1">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      name="tva"
                      value={values.tva}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2.5 pr-12 focus:outline-none focus:ring-2 focus:ring-[#ff9be4] focus:border-transparent transition-all"
                      min="0"
                      max="100"
                      step="0.01"
                      placeholder="Ex: 20"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">
                      %
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    TVA appliquée sur le montant total de la transaction
                  </p>
                </div>

                {/* Save Button */}
                <button
                  disabled={saving || !values.adminCommission || !values.tva}
                  onClick={handleSave}
                  className="w-full bg-[#ff9be4] text-white py-2.5 px-4 rounded-lg font-medium hover:bg-[#ff6bd4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <>
                      <svg
                        className="animate-spin h-5 w-5"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Sauvegarde en cours...
                    </>
                  ) : (
                    <>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-5 h-5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                        />
                      </svg>
                      Enregistrer la configuration
                    </>
                  )}
                </button>
              </CardContent>
            </Card>

            {/* Preview Calculation */}
            <Card>
              <CardHeader>
                <CardTitle>Aperçu du calcul</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-gray-600 mb-4">
                  Exemple pour une transaction de{" "}
                  <span className="font-semibold text-[#ff9be4]">
                    {exampleAmount}€
                  </span>
                </p>

                <div className="space-y-3">
                  {/* Montant initial */}
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-700">
                      Montant de la transaction
                    </span>
                    <span className="font-semibold text-gray-900">
                      {exampleAmount.toFixed(2)}€
                    </span>
                  </div>

                  {/* Commission */}
                  <div className="flex justify-between items-center p-3 bg-pink-50 rounded-lg border border-pink-100">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-700">
                        Commission admin
                      </span>
                      {values.adminCommission && (
                        <span className="text-xs text-gray-500">
                          ({values.adminCommission}%)
                        </span>
                      )}
                    </div>
                    <span className="font-semibold text-[#ff9be4]">
                      -{commissionAmount.toFixed(2)}€
                    </span>
                  </div>

                  {/* TVA */}
                  <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg border border-purple-100">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-700">TVA sur montant total</span>
                      {values.tva && (
                        <span className="text-xs text-gray-500">
                          ({values.tva}%)
                        </span>
                      )}
                    </div>
                    <span className="font-semibold text-purple-600">
                      -{tvaAmount.toFixed(2)}€
                    </span>
                  </div>

                  {/* Separator */}
                  <div className="border-t-2 border-gray-200 my-2"></div>

                  {/* Total prélevé */}
                  <div className="flex justify-between items-center p-3 bg-gray-100 rounded-lg">
                    <span className="text-sm font-medium text-gray-700">
                      Total prélevé
                    </span>
                    <span className="font-bold text-gray-900">
                      -{totalWithTva.toFixed(2)}€
                    </span>
                  </div>

                  {/* Montant couturier */}
                  <div className="flex justify-between items-center p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border-2 border-green-200">
                    <div>
                      <span className="text-sm font-medium text-gray-700 block">
                        Montant pour le couturier
                      </span>
                      <span className="text-xs text-gray-500">
                        Montant net après prélèvements
                      </span>
                    </div>
                    <span className="font-bold text-2xl text-green-600">
                      {couturierAmount.toFixed(2)}€
                    </span>
                  </div>
                </div>

                {/* Info Box */}
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"
                      />
                    </svg>
                    <div className="text-sm text-blue-800">
                      <p className="font-medium mb-1">Formule de calcul</p>
                      <p className="text-blue-700">
                        Commission = Montant × {values.adminCommission || 0}%<br/>
                        TVA = Montant × {values.tva || 0}%<br/>
                        Couturier = Montant - Commission - TVA
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardContent className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Commission actuelle
                  </p>
                  <p className="text-3xl font-bold text-[#ff9be4]">
                    {values.adminCommission || "0"}%
                  </p>
                </div>
                <div className="p-3 bg-pink-100 rounded-full">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-8 h-8 text-[#ff9be4]"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M2.25 18.75a60.07 60.07 0 0 1 15.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 0 1 3 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 0 0-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 0 1-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 0 0 3 15h-.75M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm3 0h.008v.008H18V10.5Zm-12 0h.008v.008H6V10.5Z"
                    />
                  </svg>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">TVA actuelle</p>
                  <p className="text-3xl font-bold text-purple-600">
                    {values.tva || "0"}%
                  </p>
                </div>
                <div className="p-3 bg-purple-100 rounded-full">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-8 h-8 text-purple-600"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 14.25 4.75 12m4.25 2.25 4.25-2.25M19.25 12 15 14.25m4.25-2.25a48.329 48.329 0 0 0-9-3.75m9 3.75a48.329 48.329 0 0 1-9 3.75m-9-3.75h.008v.008H4.992V12Zm9.5 0h.008v.008h-.008V12Zm9.504 0h.008v.008h-.008V12Z"
                    />
                  </svg>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      }
    />
  );
}